import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import {
  Flex,
  Label,
  Modal,
  Select,
  TextInput,
  InputButton,
  Explainer,
  Paragraph,
  useNotification
} from '@sopranodesign/pulse-components';
import Icon from '@sopranodesign/pulse-icons';
import { useQueryClient } from '@sopranodesign/pulse-hooks';

import {
  connectJira,
  deleteJiraProject,
  saveJiraProjects,
  resyncJiraProject,
  resyncAllJira,
  disconnectJira,
  recrawlJiraIssue,
  saveJiraIssueTags,
  createJiraIssueTag,
  fetchJiraIssueTags,
  fetchJiraIssueAvailableTags
} from '../api';
import WarningMessageBody from './WarningMessageBody';
import TagModal from './TagModal';
import { useTagManager } from './useTagManager';

const JiraModalManager = ({
  activeModal,
  modalProps,
  closeModal,
  projects,
  setErrorIds,
  setProjectLoadingIds,
  setIssueLoadingIds,
  setIsSyncingAll
}) => {
  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();

  const [selectedProjects, setSelectedProjects] = React.useState([]);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [connectError, setConnectError] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);

  const {
    tags,
    setTags,
    selectedTags,
    setSelectedTags,
    handleTagChange
  } = useTagManager({
    scope: 'generative_ai_jira_issues',
    createTagFn: createJiraIssueTag
  });

  useEffect(() => {
    const openAndLoadTags = async () => {
      if (activeModal === 'tags' && modalProps?.issue?.id) {
        try {
          const availableResp = await fetchJiraIssueAvailableTags();
          const availableTags = availableResp?.data?.data ?? [];
          setTags(availableTags);

          const assignedResp = await fetchJiraIssueTags(modalProps.issue.id);
          const tagList = assignedResp?.data?.data ?? [];
          const ids = tagList.map(t => t.id);

          setSelectedTags(ids);
          setIsTagModalOpen(true);
        } catch (error) {
          showError(error.message || 'Failed to load tags.');
          setIsTagModalOpen(true);
        }
      }
    };

    openAndLoadTags();

    return () => {
      if (activeModal !== 'tags') {
        setSelectedTags([]);
        setIsTagModalOpen(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModal, modalProps?.issue?.id]);

  const toggleClientSecretVisibility = () => setShowClientSecret(prev => !prev);

  const handleConnect = async () => {
    if (!clientId || !clientSecret) {
      setConnectError('Please fill in all fields before connecting to Jira.');
      return;
    }

    setConnectError('');
    setIsConnecting(true);

    // Open a blank tab immediately while still inside the click handler so
    // popup blockers treat it as a direct user gesture — the URL is only
    // known once the API call below resolves.
    const popup = window.open('', '_blank');

    try {
      const { data: redirectData } = await connectJira({ client_id: clientId, client_secret: clientSecret });
      popup.location.href = redirectData.authorize_url;
      closeModal();
    } catch (error) {
      popup.close();
      setConnectError(error.response?.data?.message ?? 'Unable to start the OAuth2 connection with Jira.');
    } finally {
      setIsConnecting(false);
    }
  };

  const handleSaveProjects = async () => {
    try {
      const payload = selectedProjects.map(project => ({
        id: project.value,
        key: project.key,
        name: project.label
      }));
      await saveJiraProjects(payload);
      queryClient.invalidateQueries({ queryKey: ['generative-ai/jira/'] });
      showSuccess('Issues fetched successfully!');
      closeModal();
      setSelectedProjects([]);
    } catch (error) {
      showError(`Failed to fetch issues: ${error.message}`);
    }
  };

  const confirmationActions = {
    delete: {
      header: 'Confirm Delete Project',
      body: (
        <WarningMessageBody
          contentHeader={`Are you sure you want to delete the project "${modalProps.project?.project_name}"?`}
          contentBody="All synced issues will be permanently removed."
          warningText="This action cannot be undone."
        />
      ),
      confirmText: 'Delete',
      handleConfirm: async () => {
        const projectId = modalProps.project.id;
        setProjectLoadingIds(prev => ({ ...prev, [projectId]: true }));
        try {
          await deleteJiraProject(projectId);
          showSuccess(`Project "${modalProps.project.project_name}" delete requested`);
          queryClient.invalidateQueries({ queryKey: ['generative-ai/jira/'] });
          closeModal();
        } catch (error) {
          showError(`Failed to delete project: ${error.message}`);
        } finally {
          setProjectLoadingIds(prev => ({ ...prev, [projectId]: false }));
        }
      }
    },
    disconnect: {
      header: 'Confirm Disconnect Jira',
      body: (
        <WarningMessageBody
          contentHeader="Are you sure you want to disconnect Jira?"
          contentBody="All synced projects and issues will be permanently removed."
          warningText="This action cannot be undone."
        />
      ),
      confirmText: 'Disconnect',
      handleConfirm: async () => {
        try {
          await disconnectJira();
          showSuccess('Jira disconnect requested');
          queryClient.invalidateQueries({ queryKey: ['generative-ai/jira/'] });
          closeModal();
        } catch (error) {
          showError(`Failed to disconnect Jira: ${error.message}`);
        }
      }
    },
    resync: {
      header: 'Confirm Resync Project',
      body: (
        <WarningMessageBody
          contentHeader={`Are you sure you want to resync the project "${modalProps.project?.project_name}"?`}
          contentBody="This will refresh its content."
        />
      ),
      confirmText: 'Resync',
      handleConfirm: async () => {
        const projectId = modalProps.project.id;
        setProjectLoadingIds(prev => ({ ...prev, [projectId]: true }));
        try {
          await resyncJiraProject(projectId);
          showSuccess('Project resync requested.');
          queryClient.invalidateQueries({ queryKey: ['generative-ai/jira/issues', projectId] });
          closeModal();
        } catch (error) {
          showError(`Failed to resync project: ${error.message}`);
        } finally {
          setProjectLoadingIds(prev => ({ ...prev, [projectId]: false }));
        }
      }
    },
    resyncAll: {
      header: 'Confirm Resync All Projects',
      body: (
        <WarningMessageBody
          contentHeader="Are you sure you want to resync all projects?"
          contentBody="This will refresh all projects' content."
        />
      ),
      confirmText: 'Resync All',
      handleConfirm: async () => {
        setIsSyncingAll(true);
        try {
          await resyncAllJira();
          showSuccess('Projects resync requested.');
          queryClient.invalidateQueries({ queryKey: ['generative-ai/jira/'] });
          closeModal();
        } catch (error) {
          showError(`Failed to resync projects: ${error.message}`);
        } finally {
          setIsSyncingAll(false);
        }
      }
    },
    recrawl: {
      header: 'Confirm Recrawl',
      body: (
        <WarningMessageBody
          contentHeader={`Are you sure you want to recrawl the issue "${modalProps.issue?.issue_key}"?`}
          contentBody="This will update its content."
        />
      ),
      confirmText: 'Recrawl',
      handleConfirm: async () => {
        const issueId = modalProps.issue.id;
        const projectId = modalProps.projectId;

        setErrorIds(prev => {
          const next = { ...prev };
          delete next[issueId];
          return next;
        });
        setIssueLoadingIds(prev => ({ ...prev, [issueId]: true }));

        try {
          await recrawlJiraIssue(issueId);
          showSuccess('Issue recrawl requested');
          queryClient.invalidateQueries({ queryKey: ['generative-ai/jira/issues', projectId] });
        } catch (error) {
          if (error.response?.status >= 500) {
            setErrorIds(prev => ({ ...prev, [issueId]: 'Failed to recrawl the issue.' }));
          }
          showError(`Failed to recrawl issue: ${error.message}`);
        } finally {
          setIssueLoadingIds(prev => ({ ...prev, [issueId]: false }));
          closeModal();
        }
      }
    }
  };

  if (!activeModal) {
    return null;
  }

  switch (activeModal) {
    case 'connect':
      return (
        <Modal
          header="Connect Jira"
          handleCancel={closeModal}
          handleConfirm={handleConnect}
          confirmText="Connect"
          cancelText="Cancel"
          loading={isConnecting}
          body={
            <Flex col gap="sm">
              {connectError && (
                <Explainer type="danger" mb hideLabel>
                  <Paragraph sm danger>
                    {connectError}
                  </Paragraph>
                </Explainer>
              )}

              <Flex fat col>
                <Label htmlFor="jiraClientId">OAuth Client ID</Label>
                <TextInput
                  id="jiraClientId"
                  value={clientId}
                  onChange={e => setClientId(e.target.value)}
                />
              </Flex>

              <Flex fat col>
                <Label htmlFor="jiraClientSecret">OAuth Client Secret</Label>
                <Flex relative>
                  <TextInput
                    id="jiraClientSecret"
                    type={showClientSecret ? 'text' : 'password'}
                    value={clientSecret}
                    onChange={e => setClientSecret(e.target.value)}
                  />
                  <InputButton type="button" onClick={toggleClientSecretVisibility}>
                    <Icon
                      size="1.25rem"
                      width="1.25rem"
                      height="1.25rem"
                      type={showClientSecret ? 'eye' : 'eyeOff'}
                    />
                  </InputButton>
                </Flex>
              </Flex>
            </Flex>
          }
        />
      );

    case 'projects':
      return (
        <Modal
          header="Jira Projects"
          handleCancel={closeModal}
          handleConfirm={handleSaveProjects}
          confirmText="Select"
          cancelText="Cancel"
          disableConfirmButton={selectedProjects.length === 0}
          body={
            <Flex col gap="sm">
              <Label htmlFor="projectSelect">Project</Label>
              <Select
                id="projectSelect"
                value={selectedProjects}
                onChange={setSelectedProjects}
                options={projects.map(project => ({ label: project.name, value: project.id, key: project.key }))}
                placeholder="Select a project"
                isMulti
              />
            </Flex>
          }
        />
      );

    case 'tags':
      return (
        <TagModal
          isVisible={isTagModalOpen}
          header="Jira Issue Tags"
          onCancel={() => {
            setIsTagModalOpen(false);
            setSelectedTags([]);
            closeModal();
          }}
          onConfirm={async () => {
            try {
              await saveJiraIssueTags({
                issueId: modalProps.issue.id,
                groupIds: selectedTags
              });

              queryClient.invalidateQueries({ queryKey: ['generative-ai/jira/issues', modalProps.projectId] });

              setIsTagModalOpen(false);
              setSelectedTags([]);
              showSuccess('Tags assigned successfully!');
              closeModal();
            } catch (error) {
              showError(error.message || 'Failed to assign tags.');
            }
          }}
          tags={tags}
          selectedTags={selectedTags}
          handleTagChange={handleTagChange}
        />
      );

    case 'confirmation': {
      const config = confirmationActions[modalProps.type];
      return (
        <Modal
          header={config.header}
          body={config.body}
          cancelText="Cancel"
          confirmText={config.confirmText}
          handleCancel={config.handleCancel ?? closeModal}
          handleConfirm={config.handleConfirm}
        />
      );
    }

    default:
      return null;
  }
};

JiraModalManager.propTypes = {
  activeModal: PropTypes.string,
  modalProps: PropTypes.object,
  closeModal: PropTypes.func.isRequired,
  projects: PropTypes.array,
  setErrorIds: PropTypes.func.isRequired,
  setProjectLoadingIds: PropTypes.func.isRequired,
  setIssueLoadingIds: PropTypes.func.isRequired,
  setIsSyncingAll: PropTypes.func.isRequired
};

export default JiraModalManager;
